import { ChevronRight } from '@mui/icons-material';
import { Button, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import ReactMarkdown from 'react-markdown';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import useUnitProvider from '/@/core/context/unit';
import { useAllCatalogProducts } from '/@/hooks/catalog';
import { useCPUseRecommendations } from '/@/hooks/crop-protection';
import { _ } from '/@/locale';
import { MuiSelect } from '/@/mui/select';
import { getSessionCountry } from '/@/store/session/selectors';
import { reverse } from '/@/utils/router';
import { type Feature, generateMatrix, generateUniqueItems } from '../../../comparator';
import { parseProductForProductCard } from '../../../components/product-card';
import type { TTab } from '../..';

const renderTableCell = (
    featureId: number,
    productId: number,
    matrix: Record<number, Record<number, React.ReactNode>>,
    addBackground: boolean
) => (
    <TableCell key={`${featureId}-${productId}`} sx={[{ background: addBackground ? '#F5F5F5' : 'inherit' }]}>
        {matrix[featureId]?.[productId]}
    </TableCell>
);

const renderProductRow = (
    feature: Feature,
    products: any[],
    matrix: Record<number, Record<number, React.ReactNode>>
) => (
    <TableRow key={feature.id}>
        <TableCell>
            <Typography variant='caption' fontWeight='bold' color='primary'>
                {feature.label}
            </Typography>
        </TableCell>
        {products.map((product, i) => renderTableCell(feature.id, product.id, matrix, i > 0))}
    </TableRow>
);

export const FeaturesMiniCard = ({ product, onClick }: { product: any; onClick: () => void }) => {
    return (
        <Stack spacing={2} p={2} sx={{ border: (t) => `1px solid ${t.palette.divider}` }}>
            <Stack spacing={1}>
                <Typography variant='h6' fontWeight='bold' color='primary'>
                    {_('Características')}
                </Typography>
            </Stack>
            <Stack spacing={1}>
                {(product?.features?.length || 0) > 0 &&
                    product.features
                        .toSorted((a, b) => _(a.value).length - _(b.value).length)
                        .slice(0, 2)
                        .map((feature) => (
                            <Stack key={feature.id} direction='row' justifyContent='space-between'>
                                <Typography variant='caption' fontWeight='bold' color='primary'>
                                    {_(feature.name)}
                                </Typography>
                                <Typography variant='body2' color='success.main'>
                                    {_(feature.value)}
                                </Typography>
                            </Stack>
                        ))}
            </Stack>
            <Typography
                variant='body1'
                color='secondary'
                sx={{ textDecoration: 'underline', cursor: 'pointer' }}
                onClick={onClick}
            >
                {_('Ver todas')}
            </Typography>
        </Stack>
    );
};

const ProductExplorationFeatures = ({ product, setTab }: { product: any; setTab: (tab: TTab) => void }) => {
    const country = useSelector(getSessionCountry);
    const navigate = useNavigate();
    const units = useUnitProvider();

    const brandSlug = product?.brand?.name?.toLowerCase() || product?.brand?.toLowerCase();
    const cropType = product?.brand?.type || 'crop_protection';

    const catalogQuery = useAllCatalogProducts({
        brand: brandSlug,
        crop: cropType,
        country: country?.code || 'AR',
        zone: product?.catalogZoneId,
        cropProtectionProductType: product?.type?.en?.toLowerCase(),
    });
    const allProducts = useMemo(
        () =>
            parseProductForProductCard(
                (catalogQuery.data?.results || []).filter((p) => p.id !== product?.id),
                units
            ),
        [catalogQuery.data, units, product?.id]
    );

    const form = useForm<{
        productIds: number[];
    }>({
        defaultValues: {
            productIds: [],
        },
    });
    useEffect(() => {
        // Default Values
        if (!product) return;
        const [parsedProduct] = parseProductForProductCard([product], units);
        if (!parsedProduct) return;
        const filteredProducts = allProducts
            .filter((p) => p.positioning?.label === parsedProduct.positioning?.label)
            .slice(0, 3);
        const productIds = filteredProducts.map((p) => p.id);
        if (productIds.length < 3) {
            productIds.push(
                ...allProducts
                    .filter((p) => !productIds.includes(p.id))
                    .slice(0, 3 - productIds.length)
                    .map((p) => p.id)
            );
        }
        form.setValue('productIds', productIds);
    }, [product, allProducts, units]);
    const selectedProducts = useMemo(() => {
        return allProducts
            .filter((p) => form.watch('productIds').includes(p.id))
            .sort((a, b) => form.watch('productIds').indexOf(a.id) - form.watch('productIds').indexOf(b.id));
    }, [allProducts, form.watch('productIds')]);

    const useRecommendationsQuery = useCPUseRecommendations(
        {
            productIdIn: [product.id, ...selectedProducts.map((p) => p.id)],
            page: 1,
            pageSize: 10000,
        },
        {
            enabled: cropType === 'crop_protection',
        }
    );

    const features = useMemo<Feature[]>(
        () =>
            [product, ...selectedProducts].flatMap((p) =>
                (p?.features || [])
                    .map((el) => ({
                        label: _(el.name || el?.feature?.name),
                        value: _(el.value),
                        productId: p.id,
                        id: el.id || el?.feature?.id,
                    }))
                    .concat(
                        (useRecommendationsQuery.data?.results || []).flatMap((r) => [
                            {
                                id: r.crop ? `${_('Cuando aplicar')} (${_(r.crop?.name)})` : _('Cuando aplicar'),
                                value: _(r.whenToApply),
                                label: r.crop ? `${_('Cuando aplicar')} (${_(r.crop?.name)})` : _('Cuando aplicar'),
                                productId: r.product.id,
                            },
                            {
                                id: r.crop ? `${_('Dosis')} (${_(r.crop?.name)})` : _('Dosis'),
                                value: _(r.dose),
                                label: r.crop ? `${_('Dosis')} (${_(r.crop?.name)})` : _('Dosis'),
                                productId: r.product.id,
                            },
                            ...(_(r?.note)
                                ? [
                                      {
                                          id: r.crop ? `${_('Nota')} (${_(r.crop?.name)})` : _('Nota'),
                                          value: _(r.note),
                                          label: r.crop ? `${_('Nota')} (${_(r.crop?.name)})` : _('Nota'),
                                          productId: r.product.id,
                                      },
                                  ]
                                : []),
                        ])
                    )
            ),
        [selectedProducts, product, useRecommendationsQuery.data?.results]
    );
    const featuresMatrix = useMemo(
        () =>
            generateMatrix(features || [], (value) => (
                <Typography variant='body2' color='primary'>
                    <ReactMarkdown>{String(value)}</ReactMarkdown>
                </Typography>
            )),
        [features]
    );
    const uniqueFeatures = useMemo(() => generateUniqueItems(features, 'id'), [features]);

    return (
        <Stack spacing={4}>
            <Stack spacing={2}>
                <Typography variant='h5' color='primary'>
                    {_('Características')}
                </Typography>
                <Typography variant='body1' color='primary.mui.inactive'>
                    {_(
                        'Resumen técnico del híbrido, con información clave para comparar con otros productos del catálogo.'
                    )}
                </Typography>
            </Stack>
            <Stack sx={{ overflowX: 'scroll' }}>
                <Table
                    sx={{
                        tableLayout: 'fixed',
                    }}
                >
                    <TableHead>
                        <TableRow>
                            <TableCell colSpan={2} sx={{ border: 'none' }} />
                            {selectedProducts.length > 0 && (
                                <TableCell
                                    colSpan={selectedProducts.length}
                                    sx={{
                                        background: '#006F9B',
                                        textAlign: 'center',
                                        px: 3,
                                        py: 0.5,
                                        border: 'none',
                                    }}
                                >
                                    <Typography variant='body2' fontWeight='bold' color='white !important'>
                                        {_('Compara con otros productos')}
                                    </Typography>
                                </TableCell>
                            )}
                        </TableRow>
                        <TableRow
                            sx={{
                                '& .MuiTableCell-root': {
                                    width: '100%',
                                },
                            }}
                        >
                            <TableCell />
                            <TableCell>
                                <Typography variant='subtitle1' fontWeight='bold' color='primary'>
                                    {product?.catalogName}
                                </Typography>
                            </TableCell>
                            {selectedProducts.map(({ id }, index) => (
                                <TableCell key={index} sx={{ background: '#F5F5F5' }}>
                                    <MuiSelect
                                        inputLabel=''
                                        onChange={(e) => {
                                            const newProductIds = [...form.watch('productIds')];
                                            newProductIds[index] = Number(e.target.value);
                                            form.setValue('productIds', newProductIds);
                                        }}
                                        value={form.watch('productIds')[index]}
                                        options={allProducts
                                            .filter((p) => p.id === id || !form.watch('productIds').includes(p.id))
                                            .map(({ id, name, catalogName }) => ({
                                                label: (
                                                    <Stack
                                                        direction='row'
                                                        spacing={1}
                                                        alignItems='center'
                                                        key={id}
                                                        value={id}
                                                    >
                                                        <Typography variant='subtitle2'>
                                                            {catalogName || name}
                                                        </Typography>
                                                    </Stack>
                                                ),
                                                value: String(id),
                                            }))}
                                    />
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {uniqueFeatures.map((feature) =>
                            renderProductRow(feature, [product, ...selectedProducts], featuresMatrix)
                        )}
                        <TableRow
                            sx={{
                                '& .MuiTableCell-root': { borderBottom: 'none' },
                            }}
                        >
                            <TableCell colSpan={2} />
                            {selectedProducts.map((p, i) => (
                                <TableCell key={p.id} sx={{ background: '#F5F5F5' }}>
                                    <Button
                                        variant='text'
                                        endIcon={<ChevronRight />}
                                        onClick={() => {
                                            setTab('main');
                                            navigate(
                                                reverse('productExploration.detail', {
                                                    params: {
                                                        type: cropType,
                                                        slug: brandSlug,
                                                        productId: p.id,
                                                    },
                                                })
                                            );
                                        }}
                                    >
                                        {_('Ver detalle')}
                                    </Button>
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableBody>
                </Table>
            </Stack>
        </Stack>
    );
};

export default ProductExplorationFeatures;
